import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowUpRight,
  IconCheck,
  IconClock,
  IconMessageCircle,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { FlagDiscussionModal } from "../workflows/FlagDiscussionModal";
import { useCaseDiscussions } from "../../hooks/useInternalDiscussions";
import {
  DiscussionStatus,
  discussionCategoryLabels,
  type InternalDiscussion,
} from "../../services/internalDiscussions.service";

dayjs.extend(relativeTime);

// ==================== TYPES ====================

interface DiscussionsCenterProps {
  caseId: string | null;
  caseNumber?: string;
}

// ==================== HELPERS ====================

function getStatusColor(status: DiscussionStatus): string {
  switch (status) {
    case DiscussionStatus.PENDING:
      return "yellow";
    case DiscussionStatus.RESOLVED:
      return "green";
    case DiscussionStatus.CANCELLED:
      return "gray";
    default:
      return "gray";
  }
}

function getStatusLabel(status: DiscussionStatus): string {
  switch (status) {
    case DiscussionStatus.PENDING:
      return "Pending";
    case DiscussionStatus.RESOLVED:
      return "Resolved";
    case DiscussionStatus.CANCELLED:
      return "Cancelled";
    default:
      return status;
  }
}

function getStatusIcon(status: DiscussionStatus) {
  switch (status) {
    case DiscussionStatus.PENDING:
      return <IconClock size={12} />;
    case DiscussionStatus.RESOLVED:
      return <IconCheck size={12} />;
    case DiscussionStatus.CANCELLED:
      return <IconX size={12} />;
    default:
      return null;
  }
}

function getCategoryColor(category: string | null): string {
  switch (category) {
    case "malignancy":
      return "red";
    case "ibd":
      return "orange";
    case "dysplasia_barretts":
      return "yellow";
    case "unusual_complex":
      return "violet";
    case "diagnostic_uncertainty":
      return "grape";
    case "educational":
      return "teal";
    default:
      return "gray";
  }
}

// ==================== DISCUSSION CARD ====================

function DiscussionCard({
  discussion,
  caseId,
}: {
  discussion: InternalDiscussion;
  caseId: string;
}) {
  const handleOpen = () => {
    window.open(
      `/discussion-queue/${caseId}/${discussion.id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Card
      withBorder
      p="md"
      shadow="xs"
      style={{ cursor: "pointer" }}
      onClick={handleOpen}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          {/* Badges row */}
          <Group gap={6} wrap="wrap">
            {discussion.category && (
              <Badge
                size="sm"
                variant="light"
                color={getCategoryColor(discussion.category)}
              >
                {discussionCategoryLabels[discussion.category] ??
                  discussion.category}
              </Badge>
            )}
            <Badge
              size="sm"
              variant="filled"
              color={getStatusColor(discussion.status)}
              leftSection={getStatusIcon(discussion.status)}
            >
              {getStatusLabel(discussion.status)}
            </Badge>
            {discussion.isUrgent && (
              <Badge size="sm" variant="filled" color="red">
                Urgent
              </Badge>
            )}
          </Group>

          {/* Meta info */}
          <Text size="sm" c="dimmed">
            Initiated by{" "}
            <Text span c="dark" fw={500}>
              {discussion.initiatedBy?.fullName ?? "Unknown"}
            </Text>{" "}
            &middot; {dayjs(discussion.createdAt).fromNow()}
          </Text>

          {discussion.assignedTo && (
            <Text size="sm" c="dimmed">
              Assigned to:{" "}
              <Text span c="dark">
                {discussion.assignedTo.fullName}
              </Text>
            </Text>
          )}

          {discussion.status === DiscussionStatus.RESOLVED &&
            discussion.resolvedBy && (
              <Text size="xs" c="dimmed">
                Resolved by {discussion.resolvedBy.fullName}
                {discussion.resolvedAt
                  ? ` · ${dayjs(discussion.resolvedAt).format("MMM D, YYYY")}`
                  : ""}
              </Text>
            )}

          {discussion.status === DiscussionStatus.CANCELLED &&
            discussion.cancelledBy && (
              <Text size="xs" c="dimmed">
                Cancelled by {discussion.cancelledBy.fullName}
                {discussion.cancelledAt
                  ? ` · ${dayjs(discussion.cancelledAt).format("MMM D, YYYY")}`
                  : ""}
              </Text>
            )}
        </Stack>

        <Tooltip label="Open in new tab" withArrow>
          <Box
            style={{ color: "var(--mantine-color-blue-6)", flexShrink: 0 }}
            pt={2}
          >
            <IconArrowUpRight size={16} />
          </Box>
        </Tooltip>
      </Group>
    </Card>
  );
}

// ==================== COMPONENT ====================

export function DiscussionsCenter({
  caseId,
  caseNumber,
}: DiscussionsCenterProps) {
  const [flagModalOpened, { open: openFlagModal, close: closeFlagModal }] =
    useDisclosure(false);

  const { data: discussions = [], isLoading } = useCaseDiscussions(
    caseId || ""
  );

  if (!caseId) {
    return (
      <Box>
        <Text c="dimmed" ta="center" size="sm" py="xl">
          Save the case first to view discussions
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconMessageCircle size={20} color="var(--mantine-color-blue-6)" />
            <Text size="lg" fw={600}>
              Discussions
            </Text>
            {discussions.length > 0 && (
              <Badge size="sm" variant="light" color="blue">
                {discussions.length}
              </Badge>
            )}
          </Group>
          <Button
            size="sm"
            leftSection={<IconPlus size={14} />}
            onClick={openFlagModal}
          >
            Flag for Discussion
          </Button>
        </Group>

        <Divider />

        {/* List */}
        {isLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : discussions.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconMessageCircle
                size={40}
                color="var(--mantine-color-gray-4)"
              />
              <Text c="dimmed" size="sm">
                No discussions for this case
              </Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={openFlagModal}
              >
                Flag for Discussion
              </Button>
            </Stack>
          </Center>
        ) : (
          <Stack gap="sm">
            {discussions.map((discussion) => (
              <DiscussionCard
                key={discussion.id}
                discussion={discussion}
                caseId={caseId}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <FlagDiscussionModal
        opened={flagModalOpened}
        onClose={closeFlagModal}
        caseId={caseId}
        caseNumber={caseNumber ?? ""}
      />
    </>
  );
}
